"use client"

import type React from "react"

import { useState, useEffect } from "react"
import { observer } from "mobx-react-lite"
import {
  ChevronLeft,
  ChevronRight,
  Users,
  TrendingUp,
  Info,
  Crown,
  Heart,
  CheckCircle,
  Clock,
  Target,
} from "lucide-react"
import { useAppContext } from "../../../../shared/functions/Context"
import DoughnutChart from "../../dashboard/Charts/DoughnutChart"

interface SlideData {
  title: string
  subtitle: string
  data: { name: string; value: number }[]
  colors: string[]
  label: string
  percentage: string
  tooltipText: string
  icon: React.ReactNode
  gradient: string
  accentColor: string
}

const UserRatingStatus = observer(() => {
  const { store } = useAppContext()
  const me = store.auth.me?.asJson

  const leadershipRatingsReceived = store.leadershipRating.all.filter(
    (rating) => rating.asJson.rateeId === me?.uid,
  ).length

 const totalToRateCurrentUserLeadership = me
  ? store.ratingAssignments.all
      .filter((assignment) => assignment.asJson.isActive)
      .flatMap((assignment) => assignment.asJson.feedbackAssignments[me.uid] || []).length
  : 0;

const valueRatingsReceived = me
  ? store.valueRating.all.filter((rating) => rating.asJson.rateeId === me.uid).length
  : 0;

const totalToRateCurrentUserValue = me
  ? store.ratingAssignments.all
      .filter((assignment) => assignment.asJson.isActive)
      .flatMap((assignment) => assignment.asJson.feedbackAssignments[me.uid] || []).length
  : 0;

  const [currentSlide, setCurrentSlide] = useState(0)
  const [isAutoPlaying, setIsAutoPlaying] = useState(true)

  const slides: SlideData[] = [
    {
      title: "Leadership Ratings",
      subtitle: "Feedback from your team",
      data: [
        { name: "Completed", value: leadershipRatingsReceived },
        { name: "Pending", value: Math.max(0, totalToRateCurrentUserLeadership - leadershipRatingsReceived) },
      ],
      colors: ["#3B82F6", "#E2E8F0"],
      label: "Leadership Ratings Progress",
      percentage:
        leadershipRatingsReceived === 0
          ? "0.00"
          : ((leadershipRatingsReceived / totalToRateCurrentUserLeadership) * 100).toFixed(1),
      tooltipText: "Percentage of leadership evaluations completed by your assigned reviewers",
      icon: <Crown className="h-6 w-6" />,
      gradient: "from-blue-500 to-indigo-600",
      accentColor: "blue",
    },
    {
      title: "Values Ratings",
      subtitle: "Core values assessment",
      data: [
        { name: "Completed", value: valueRatingsReceived },
        { name: "Pending", value: Math.max(0, totalToRateCurrentUserValue - valueRatingsReceived) },
      ],
      colors: ["#8B5CF6", "#E2E8F0"],
      label: "Values Ratings Progress",
      percentage:
        valueRatingsReceived === 0 ? "0.00" : ((valueRatingsReceived / totalToRateCurrentUserValue) * 100).toFixed(1),
      tooltipText: "Percentage of values evaluations completed by your assigned reviewers",
      icon: <Heart className="h-6 w-6" />,
      gradient: "from-purple-500 to-violet-600",
      accentColor: "purple",
    },
  ]

  // Auto-play functionality
  useEffect(() => {
    if (!isAutoPlaying) return

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length)
    }, 5000)

    return () => clearInterval(interval)
  }, [isAutoPlaying])

  const nextSlide = () => {
    setIsAutoPlaying(false)
    setCurrentSlide((prev) => (prev + 1) % slides.length)
  }

  const prevSlide = () => {
    setIsAutoPlaying(false)
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length)
  }

  const goToSlide = (index: number) => {
    setIsAutoPlaying(false)
    setCurrentSlide(index)
  }

  const currentSlideData = slides[currentSlide]
  const completionRate = Number.parseFloat(currentSlideData.percentage)

  if (!me) {
    return (
      <div className="flex items-center justify-center h-96 bg-slate-50 rounded-2xl">
        <div className="text-center space-y-4">
          <Users className="h-12 w-12 text-slate-400 mx-auto" />
          <p className="text-slate-600">No user data available</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-purple-100 rounded-lg">
            <Users className="h-5 w-5 text-purple-600" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-slate-800">Rating Progress</h3>
            <p className="text-sm text-slate-600">Your feedback collection status</p>
          </div>
        </div>

        {/* Auto-play toggle */}
        <button
          onClick={() => setIsAutoPlaying(!isAutoPlaying)}
          className={`p-2 rounded-lg transition-colors ${
            isAutoPlaying
              ? "bg-green-100 text-green-600 hover:bg-green-200"
              : "bg-slate-100 text-slate-600 hover:bg-slate-200"
          }`}
          title={isAutoPlaying ? "Pause auto-play" : "Resume auto-play"}
        >
          {isAutoPlaying ? <Clock className="h-4 w-4" /> : <Target className="h-4 w-4" />}
        </button>
      </div>

      {/* Main carousel container */}
      <div className="relative bg-gradient-to-br from-slate-50 to-white rounded-2xl p-6 shadow-lg border border-slate-200/50 min-h-[400px] overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 opacity-5">
          <div
            className={`absolute top-0 right-0 w-32 h-32 bg-gradient-to-br ${currentSlideData.gradient} rounded-full blur-3xl`}
          />
          <div
            className={`absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr ${currentSlideData.gradient} rounded-full blur-2xl`}
          />
        </div>

        {/* Slide content */}
        <div className="relative z-10 h-full flex flex-col">
          {/* Slide header */}
          <div className="text-center mb-6">
            <div
              className={`inline-flex items-center gap-3 bg-gradient-to-r ${currentSlideData.gradient} text-white px-4 py-2 rounded-full mb-4 shadow-lg`}
            >
              {currentSlideData.icon}
              <span className="font-semibold">{currentSlideData.title}</span>
            </div>
            <p className="text-slate-600 text-sm">{currentSlideData.subtitle}</p>
          </div>

          {/* Progress stats */}
          <div className="flex items-center justify-center gap-8 mb-6">
            <div className="text-center">
              <div className="flex items-center gap-2 mb-1">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <span className="text-2xl font-bold text-slate-800">{currentSlideData.data[0].value}</span>
              </div>
              <p className="text-xs text-slate-600 uppercase tracking-wide">Completed</p>
            </div>

            <div className="w-px h-8 bg-slate-300" />

            <div className="text-center">
              <div className="flex items-center gap-2 mb-1">
                <Clock className="h-4 w-4 text-amber-600" />
                <span className="text-2xl font-bold text-slate-800">{currentSlideData.data[1].value}</span>
              </div>
              <p className="text-xs text-slate-600 uppercase tracking-wide">Pending</p>
            </div>

            <div className="w-px h-8 bg-slate-300" />

            <div className="text-center">
              <div className="flex items-center gap-2 mb-1">
                <TrendingUp className="h-4 w-4 text-blue-600" />
                <span className="text-2xl font-bold text-slate-800">{currentSlideData.percentage}%</span>
              </div>
              <p className="text-xs text-slate-600 uppercase tracking-wide">Complete</p>
            </div>
          </div>

          {/* Progress bar */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-slate-700">Progress</span>
              <div className="flex items-center gap-1">
                <Info className="h-3 w-3 text-slate-400" />
                <span className="text-xs text-slate-500">{currentSlideData.tooltipText}</span>
              </div>
            </div>
            <div className="w-full bg-slate-200 rounded-full h-3 overflow-hidden">
              <div
                className={`h-full bg-gradient-to-r ${currentSlideData.gradient} rounded-full transition-all duration-1000 ease-out relative`}
                style={{ width: `${Math.min(completionRate, 100)}%` }}
              >
                <div className="absolute inset-0 bg-white/20 animate-pulse" />
              </div>
            </div>
          </div>

          {/* Chart */}
          <div className="flex-1 flex items-center justify-center">
            <div className="w-full max-w-xs">
              <DoughnutChart
                data={currentSlideData.data}
                colors={currentSlideData.colors}
                label={currentSlideData.label}
              />
            </div>
          </div>
        </div>

        {/* Navigation arrows */}
        <button
          onClick={prevSlide}
          className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-white/90 backdrop-blur-sm rounded-full shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-110 group"
        >
          <ChevronLeft className="h-5 w-5 text-slate-600 group-hover:text-slate-800" />
        </button>

        <button
          onClick={nextSlide}
          className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-white/90 backdrop-blur-sm rounded-full shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-110 group"
        >
          <ChevronRight className="h-5 w-5 text-slate-600 group-hover:text-slate-800" />
        </button>
      </div>

      {/* Slide indicators */}
      <div className="flex items-center justify-center gap-3">
        {slides.map((slide, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all duration-300 ${
              currentSlide === index
                ? `bg-gradient-to-r ${slide.gradient} text-white shadow-lg scale-105`
                : "bg-slate-100 text-slate-600 hover:bg-slate-200 hover:scale-105"
            }`}
          >
            {slide.icon}
            <span className="text-sm font-medium hidden sm:inline">{slide.title}</span>
            <span className="text-xs bg-white/20 px-2 py-1 rounded-full">{slide.percentage}%</span>
          </button>
        ))}
      </div>

      {/* Summary stats */}
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
          <div className="flex items-center gap-3 mb-2">
            <Crown className="h-5 w-5 text-blue-600" />
            <span className="font-semibold text-blue-800">Leadership</span>
          </div>
          <p className="text-2xl font-bold text-blue-900">{slides[0].percentage}%</p>
          <p className="text-sm text-blue-600">
            {leadershipRatingsReceived} of {totalToRateCurrentUserLeadership} completed
          </p>
        </div>

        <div className="bg-purple-50 rounded-xl p-4 border border-purple-100">
          <div className="flex items-center gap-3 mb-2">
            <Heart className="h-5 w-5 text-purple-600" />
            <span className="font-semibold text-purple-800">Values</span>
          </div>
          <p className="text-2xl font-bold text-purple-900">{slides[1].percentage}%</p>
          <p className="text-sm text-purple-600">
            {valueRatingsReceived} of {totalToRateCurrentUserValue} completed
          </p>
        </div>
      </div>
    </div>
  )
})

export default UserRatingStatus
